// Reopen-destination derivation — where a closed/lost project lands when a rep
// reopens it from the project header's Reopen affordance (2026-07-27).
//
// The naive choice — reset to "opportunity" — is WRONG for a won/closed deal:
// in STAGE_ORDER (services/projects.ts) `closed` ranks AFTER `invoiced`, and the
// commonsku migration (migration/upsert-projects.ts) maps historical won/closed
// deals to `closed` with a real Quote + invoices behind them. Dropping
// such a deal back to `opportunity` invites fresh presentation/estimate/SO
// creation that collides with the one-Sales-Order-per-project rule downstream.
//
// So reopen returns the deal to its MOST-ADVANCED existing document, capped at
// `sales_order` (never in_production/invoiced — reopening is for active work,
// not to re-assert a fulfilled state). This mirrors the forward derivation
// advanceProjectStage uses, computed purely from the doc counts the
// /projects/:id detail payload already carries — so the client derives it with
// no extra fetch and the server-side stage move is a plain PATCH.
//
// DB-free on purpose: the offline gate EXECUTES this against fixtures (incl. a
// closed-with-Sales-Order project) rather than regex-matching component source,
// and the frontend imports this exact function so the destination can't drift.

export interface ReopenDocCounts {
  /** Presentation docs on the project (cancellation-excluding truth count). */
  presentations: number;
  /** Estimate docs (orders at quote stage), same truth count. */
  estimates: number;
  /** Quote docs (committed+ orders), same truth count. */
  salesOrders: number;
}

/** The only stages a reopen may land on — active working stages, never a
 *  fulfilled/terminal one. */
export type ReopenStage =
  | "opportunity"
  | "presentation"
  | "estimate"
  | "sales_order";

/**
 * Most-advanced existing document → the stage a reopen returns the project to.
 * Quote > Estimate > Presentation > (nothing) Opportunity. Capped at
 * sales_order so a fully-invoiced closed deal reopens onto its Quote (the
 * live working surface) instead of the top of the funnel.
 */
export function deriveReopenStage(counts: ReopenDocCounts): ReopenStage {
  if (counts.salesOrders > 0) return "sales_order";
  if (counts.estimates > 0) return "estimate";
  if (counts.presentations > 0) return "presentation";
  return "opportunity";
}
